/*!
 * Resolve Customer PO Number from sales order header attributes (checkout attribute: Customer PO Number).
 */
import { IActionContext } from '@msdyn365-commerce/core';
import { AttributeValueBase } from '@msdyn365-commerce/retail-proxy';
import { OrderShipments, SalesOrder } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

export type SalesOrderPoNumberSource = Pick<SalesOrder, 'SalesId' | 'AttributeValues'> | OrderShipments;
import { getSalesOrderDetailsBySalesIdAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/SalesOrdersDataActions.g';

const PO_ATTRIBUTE_NAMES = ['Customer PO Number'];

export const getSalesOrderPoNumber = (attributes: AttributeValueBase[] | undefined): string | undefined => {
    if (!attributes?.length) {
        return undefined;
    }

    const match = attributes.find(attribute => PO_ATTRIBUTE_NAMES.some(name => attribute.Name?.toLowerCase() === name.toLowerCase())) as
        | { TextValue?: string }
        | undefined;

    const value = match?.TextValue?.trim();
    return value ? value : undefined;
};

const toRetailProxyContext = (actionContext: IActionContext): import('@msdyn365-commerce/retail-proxy').IContext => ({
    callerContext: (actionContext as unknown) as import('@msdyn365-commerce/retail-proxy').ICallerContext
});

/**
 * Returns PO from list payload when present; otherwise hydrates the order via OOB GetSalesOrderDetailsBySalesId.
 */
export const resolveSalesOrderPoNumberAsync = async (
    actionContext: IActionContext | undefined,
    salesOrder: SalesOrderPoNumberSource
): Promise<string | undefined> => {
    const attributeValues = 'AttributeValues' in salesOrder ? salesOrder.AttributeValues : undefined;
    const fromList = getSalesOrderPoNumber(attributeValues);
    if (fromList) {
        return fromList;
    }

    const salesId = salesOrder.SalesId?.trim();
    if (!salesId || !actionContext) {
        return undefined;
    }

    try {
        const details = await getSalesOrderDetailsBySalesIdAsync(toRetailProxyContext(actionContext), salesId);
        return getSalesOrderPoNumber(details?.AttributeValues);
    } catch {
        return undefined;
    }
};
